import {
  Alert,
  Autocomplete,
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { AutocompleteMulti } from "components/AutocompleteMulti/AutocompleteMulti"
import { StyledButton } from "components/Button/StyledButton"
import { ReportNodeImageBox } from "components/ReportNodeImage/ReportNodeImageBox"
import { ReportNodeImageEditDialog } from "components/ReportNodeImage/ReportNodeImageEditDialog"
import { ReportNodeTableBox } from "components/ReportNodeTable/ReportNodeTableBox"
import { ReportNodeTableEditDialog } from "components/ReportNodeTable/ReportNodeTableEditDialog"
import { ReportNodeTextBox } from "components/ReportNodeText/ReportNodeTextBox"
import { ReportNodeTextEditDialog } from "components/ReportNodeText/ReportNodeTextEditDialog"
import {
  ChapterType,
  DataNode,
  ExaminationConfig,
  NodeDataType,
  Norm,
  TextBlock,
} from "graphql/types"
import { useStoreActions, useStoreState } from "hooks/storeHooks"
import { SyntheticEvent, useEffect, useState } from "react"
import { getChapterName } from "store/reportChaptersTemplate"
import { isNodeImage, isNodeTable, isNodeText } from "store/typeGuards"
import { v4 } from "uuid"

type AddTextBlockDialogProps = {
  open: boolean
  onClose: () => void
  formState: TextBlock
  onSubmit: (arg: TextBlock) => void
}

type ValidationErrors = {
  block?: string
  name?: string
}

export const AddTextBlockDialog = (props: AddTextBlockDialogProps) => {
  const { open, onClose, formState, onSubmit } = props

  const [localFormState, setLocalFormState] = useState<TextBlock>(formState)
  const [newNode, setNewNode] = useState<DataNode>()
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const { examinationConfigs, norms } = useStoreState((state) => state)
  const { fetchExaminationConfigs, fetchNorms } = useStoreActions((actions) => actions)

  useEffect(() => {
    const loadExaminationConfigs = async () => {
      try {
        await fetchExaminationConfigs({ fresh: false })
      } catch (err) {
        console.error("Error loading examination configs:", err)
      }
    }
    loadExaminationConfigs()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const loadNorms = async () => {
      try {
        await fetchNorms({ fresh: false })
      } catch (err) {
        console.error("Error loading norms:", err)
      }
    }
    loadNorms()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    setLocalFormState(formState)
    setValidationErrors({}) // Clear errors when form state changes
    setHasSubmitted(false)
  }, [formState])

  const validateForm = (state: TextBlock = localFormState): ValidationErrors => {
    const errors: ValidationErrors = {}

    if (!state.block || state.block.length === 0) {
      errors.block = "Block ist erforderlich"
    }

    if (!state.name?.trim()) {
      errors.name = "Name ist erforderlich"
    }

    return errors
  }

  const isFormValid = (): boolean => {
    const errors = validateForm(localFormState)
    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const setFieldValue = (name: string | undefined, value: unknown) => {
    if (!name) {
      return
    }

    setLocalFormState((prevState) => {
      const nextState = {
        ...prevState,
        [name]: value,
      } as TextBlock
      if (hasSubmitted) {
        setValidationErrors(validateForm(nextState))
      }
      return nextState
    })
  }

  const handleChapterChange = (_: any, value: ChapterType | null) => {
    if (value) setLocalFormState((prevState) => ({ ...prevState, chapter: value }))
  }

  const handleFormChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<string>
  ) => {
    const { name, value } = e.target
    setFieldValue(name, value)
  }

  const handleNameInput = (event: React.FormEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    setFieldValue(name, value)
  }

  const handleNameBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.currentTarget
    setFieldValue(name, value)
  }

  const handleExamChange = (_: SyntheticEvent<Element, Event>, value: ExaminationConfig[]) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      examinations: value.map((item) => item.id),
    }))
  }

  const handleNormChange = (_: SyntheticEvent<Element, Event>, value: Norm[]) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      norms: value.map((item) => item.id),
    }))
  }

  const handleIsTestRelevantChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name } = event.target
    setFieldValue(name ?? "isTestRelevant", event.target.checked)
  }

  const handleSave = () => {
    setHasSubmitted(true)
    if (isFormValid()) {
      onSubmit(localFormState)
    }
  }

  const handleClose = () => {
    setLocalFormState(formState)
    setValidationErrors({})
    setHasSubmitted(false)
    onClose()
  }

  const handleOpenAddNode = (type: NodeDataType) => {
    const newNode: DataNode = {
      id: v4(),
      dataType: type,
      editable: true,
      deletable: true,
      image: [],
      imageCaption: [],
      imageNumber: [],
      tableBody: [],
      tableCaption: "",
      tableHeader: [],
      tableNumber: 1,
      text: "",
    }
    setNewNode(newNode)
  }

  const handleCloseAddNode = () => {
    setNewNode(undefined)
  }

  const handleAddNode = () => {
    if (newNode) {
      const isOldBlock = localFormState.block.find((item) => item.id === newNode?.id)
      if (isOldBlock) {
        setLocalFormState((prevState) => ({
          ...prevState,
          block: prevState.block.map((item) => (item.id === newNode?.id ? newNode : item)),
        }))
      } else {
        setLocalFormState((prevState) => ({
          ...prevState,
          block: [...prevState.block, newNode],
        }))
      }
      handleCloseAddNode()
    }
  }

  const hasValidationErrors = Object.keys(validationErrors).length > 0

  const handleDeleteNode = (id: string) => {
    setLocalFormState((prevState) => ({
      ...prevState,
      block: prevState.block.filter((item) => item.id !== id),
    }))
  }

  const handleEditNode = (node: DataNode) => {
    setNewNode(node)
  }

  return (
    <Dialog open={open} maxWidth="md" fullWidth>
      <DialogTitle>
        {localFormState.id !== "NEW" ? "Textvorlage bearbeiten" : "Textvorlage hinzufügen"}
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {hasValidationErrors && (
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
              Bitte korrigieren Sie die folgenden Fehler:
            </Typography>
            <Stack spacing={0.5}>
              {Object.entries(validationErrors).map(([field, error]) => (
                <Typography key={field} variant="body2">
                  • {error}
                </Typography>
              ))}
            </Stack>
          </Alert>
        )}
        <TextField
          size="small"
          name="name"
          label="Name"
          type="text"
          fullWidth
          value={localFormState.name}
          onChange={handleFormChange}
          onInput={handleNameInput}
          onBlur={handleNameBlur}
          required
          error={!!validationErrors.name}
          helperText={validationErrors.name}
        />

        <Autocomplete
          size="small"
          options={Object.values(ChapterType).sort((a, b) =>
            getChapterName(a).localeCompare(getChapterName(b))
          )}
          value={localFormState.chapter}
          onChange={handleChapterChange}
          getOptionLabel={(option) => getChapterName(option)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Kapitel"
              fullWidth
              required
              helperText={
                "Wählen Sie das Kapitel aus, dem diese Textvorlage zugeordnet werden soll."
              }
            />
          )}
          fullWidth
        />

        <AutocompleteMulti
          options={examinationConfigs.filter((e) => !e.isObsolete)}
          value={examinationConfigs
            .filter((e) => !e.isObsolete)
            .filter((item) => localFormState.examinations.includes(item.id))}
          onChange={handleExamChange}
          label={"Prüfverfahren"}
          required
          helperText={
            "Wählen Sie die Prüfverfahren aus, denen diese Textvorlage zugeordnet werden soll. Wenn Sie keine Prüfverfahren auswählen, wird der Textvorlage für alle Prüfverfahren angezeigt."
          }
        />

        <AutocompleteMulti
          options={norms.filter((n) => !n.isDeleted)}
          value={norms
            .filter((n) => !n.isDeleted)
            .filter((item) => localFormState.norms.includes(item.id))}
          onChange={handleNormChange}
          label={"Normen"}
          helperText={
            "Wählen Sie die Normen aus, denen dieser Textvorlage zugeordnet werden soll. Wenn Sie keine Normen auswählen, wird der Textvorlage für alle Normen angezeigt."
          }
        />

        <FormControlLabel
          control={
            <Checkbox
              name="isTestRelevant"
              checked={localFormState.isTestRelevant || false}
              onChange={handleIsTestRelevantChange}
            />
          }
          label="Testrelevant (Testparameter)"
        />

        <Box sx={{ width: "100%", display: "flex", flexDirection: "row", gap: 2 }}>
          <StyledButton
            sx={{ flex: 1 }}
            buttonType="add"
            customText="Text hinzufügen"
            onClick={() => handleOpenAddNode(NodeDataType.TEXT)}
          />
          <StyledButton
            sx={{ flex: 1 }}
            buttonType="add"
            customText="Tabelle hinzufügen"
            onClick={() => handleOpenAddNode(NodeDataType.TABLE)}
          />
          <StyledButton
            sx={{ flex: 1 }}
            buttonType="add"
            customText="Bild hinzufügen"
            onClick={() => handleOpenAddNode(NodeDataType.IMAGE)}
          />
        </Box>

        {localFormState.block.length > 0 && (
          <Box>
            {localFormState.block.map((item) => (
              <Box
                key={item.id}
                sx={{
                  gap: 1,
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-between",
                }}
              >
                {isNodeText(item) && <ReportNodeTextBox data={item} />}
                {isNodeTable(item) && <ReportNodeTableBox data={item} />}
                {isNodeImage(item) && <ReportNodeImageBox data={item} />}
                <Box sx={{ display: "flex", gap: 1 }}>
                  <StyledButton
                    buttonType="edit"
                    size="small"
                    iconOnly={true}
                    onClick={() => handleEditNode(item)}
                  />
                  <StyledButton
                    buttonType="delete"
                    size="small"
                    iconOnly={true}
                    onClick={() => handleDeleteNode(item.id)}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <StyledButton buttonType="cancel" onClick={handleClose} />
        <StyledButton buttonType="save" onClick={handleSave} disabled={hasValidationErrors} />
      </DialogActions>

      {newNode && isNodeText(newNode) && (
        <ReportNodeTextEditDialog
          open={newNode.dataType === NodeDataType.TEXT}
          onClose={handleCloseAddNode}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}

      {newNode && isNodeTable(newNode) && (
        <ReportNodeTableEditDialog
          open={newNode.dataType === NodeDataType.TABLE}
          onClose={handleCloseAddNode}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}

      {newNode && isNodeImage(newNode) && (
        <ReportNodeImageEditDialog
          open={newNode.dataType === NodeDataType.IMAGE}
          onClose={handleCloseAddNode}
          node={newNode}
          setNode={setNewNode}
          onSave={handleAddNode}
        />
      )}
    </Dialog>
  )
}
